import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Bell, Check } from 'lucide-react';
import axiosInstance from '../lib/axios';
import { toast } from 'sonner';
import { formatDate } from '../lib/utils';
import { useTranslation } from 'react-i18next';

const Notifications = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  // Helper function to translate notification titles and messages
  const translateNotification = (title, message) => {
    // Translate common notification titles
    let translatedTitle = title;
    if (title === "New Ticket Assigned" || title === "Ticket Assigned") {
      translatedTitle = t('notifications.ticketAssigned');
    } else if (title === "Ticket Status Updated") {
      translatedTitle = t('notifications.statusUpdated');
    } else if (title === "New comment on ticket") {
      translatedTitle = t('notifications.newComment');
    }

    // Translate message patterns
    let translatedMessage = message;
    if (message.includes("You have been assigned")) {
      translatedMessage = message.replace("You have been assigned to ticket:", t('notifications.assignedTo'));
      translatedMessage = translatedMessage.replace("You have been assigned ticket:", t('notifications.assignedTo'));
    } else if (message.includes("commented on ticket")) {
      const parts = message.split(" commented on ticket: ");
      if (parts.length === 2) {
        translatedMessage = `${parts[0]} ${t('notifications.commentedOn')} ${parts[1]}`;
      }
    } else if (message.includes("status changed to")) {
      const parts = message.match(/(.*) status changed to (.*)/);
      if (parts) {
        translatedMessage = `${parts[1]} ${t('notifications.statusChangedTo')} ${parts[2]}`;
      }
    }

    return { title: translatedTitle, message: translatedMessage };
  };

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      const response = await axiosInstance.get('/notifications');
      setNotifications(response.data);
    } catch (error) {
      toast.error(t('messages.error.fetchFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (notificationId) => {
    try {
      await axiosInstance.put(`/notifications/${notificationId}/read`);
      setNotifications(notifications.filter(n => n.id !== notificationId));
      toast.success(t('messages.success.updated'));
    } catch (error) {
      toast.error(t('messages.error.failed'));
    }
  };

  const handleNotificationClick = (notification) => {
    if (notification.ticket_id) {
      handleMarkAsRead(notification.id);
      navigate(`/tickets/${notification.ticket_id}`);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-4xl font-black tracking-tight">{t('navigation.notifications')}</h1>
        <p className="text-muted-foreground mt-2">{t('notifications.viewAllYour')}</p>
      </div>

      {notifications.length === 0 ? (
        <Card className="p-12">
          <div className="text-center">
            <Bell className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
            <h3 className="text-xl font-bold mb-2">{t('notifications.noNotifications')}</h3>
            <p className="text-muted-foreground">{t('notifications.allCaughtUp')}</p>
          </div>
        </Card>
      ) : (
        <div className="space-y-3">
          {notifications.map((notification) => {
            const { title, message } = translateNotification(notification.title, notification.message);
            return (
              <Card 
                key={notification.id}
                className={`cursor-pointer hover:border-primary transition-colors ${
                  !notification.read ? 'bg-primary/5 border-primary/20' : ''
                }`}
                onClick={() => handleNotificationClick(notification)}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Bell className="w-4 h-4 text-primary" />
                        <h3 className="font-semibold">{title}</h3>
                        {!notification.read && (
                          <Badge variant="default" className="text-xs">{t('notifications.new')}</Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground mb-2">
                        {message}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {formatDate(notification.created_at)}
                      </p>
                    </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleMarkAsRead(notification.id);
                    }}
                    className="flex-shrink-0"
                  >
                    <Check className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Notifications;
